import { z } from 'zod';
/**
 * The stale-preview guard's wire fields (handoff 0010 §5), in a file of their own so every
 * contract module that owns a money-changing request body — `entitlement.ts` (checkout, add an
 * app), `location.ts` (create / delete a location), `member.ts` (invite, status) and
 * `price-preview.ts` — can spread them in without an import cycle (`price-preview.ts` already
 * imports `entitlement.ts` and `location.ts`). Re-exported from `price-preview.ts`, which is the
 * one place the index exports them from.
 */
/** Upper bound on any amount a preview or an expectation may carry, in minor units — $10M.
 *  Far above any real invoice; exists only so a hostile body cannot send a 300-digit number. */
export const MAX_PREVIEW_AMOUNT_MINOR_UNITS = 1_000_000_000;
export const minorUnitsSchema = z.number().int().min(0).max(MAX_PREVIEW_AMOUNT_MINOR_UNITS);
/** Upper bound on a `prorationDate` (epoch seconds) — 2100-01-01. Only so a hostile body cannot
 *  send an absurd number; the server refuses anything in the future anyway. */
export const MAX_PRORATION_DATE_SECONDS = 4_102_444_800;
/** An instant in whole EPOCH SECONDS — the granularity the billing provider prorates to. */
export const prorationDateSchema = z.number().int().min(0).max(MAX_PRORATION_DATE_SECONDS);
/**
 * The stale-preview guard's fields — the amounts the user was SHOWN. A confirming route's body
 * schema spreads these in (`z.object({ planCode, interval, ...expectedPriceFields }).strict()`),
 * so forgetting them is a type error at the call site and a 400 on the wire.
 *
 * ALWAYS REQUIRED on a money-changing route, even when the change turns out to cost nothing (a
 * tenant that is not billed for the dimension previews `0` / `0`, and confirms with exactly
 * that): the contract is uniform, so no route has to decide per tenant whether to ask.
 */
export const expectedPriceFields = {
    expectedDueNow: minorUnitsSchema,
    expectedRecurring: minorUnitsSchema,
    /**
     * Handoff 0014 §2: the preview's `prorationDate`, echoed back. The server re-prices AT this
     * instant (and the provider charges at it), so the confirm compares like with like instead of
     * drifting by a cent of proration. Refused (409 `PRICE_CHANGED`, `reason: 'preview_expired'`)
     * when it is in the future or more than 10 minutes old.
     *
     * OPTIONAL FOR ONE RELEASE: a browser tab still running the previous SPA bundle omits it; the
     * server then prices at its own clock, as before, and logs a warning. Make it required once
     * every client sends it.
     */
    prorationDate: prorationDateSchema.optional(),
};
export const expectedPriceSchema = z.object(expectedPriceFields).strict();
//# sourceMappingURL=expected-price.js.map